import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_URL } from '../config/api';

// Interface for standard backend API responses
export interface ApiResponse<T> {
  message: string;
  status: string;
  data: T;
}

export interface Tenant {
  tenantId: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SolutionTemplate {
  code: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssignedSolution {
  solutionId: string;
  solutionCode: string;
  name: string;
  tenantId: string;
  createdAt?: string;
}

export interface Unit {
  unitId: string;
  tenantId: string;
  solutionId: string;
  name: string;
  disabled: boolean;
  useIntegratedDatabase?: boolean;
  dbHost?: string;
  dbPort?: number;
  dbDatabase?: string;
  dbSchema?: string;
  dbUsername?: string;
  hasDbPassword?: boolean;
  dbConnectionTimeout?: number;
  dbSslMode?: string;
  hasSslRootCert?: boolean;
  hasSslCertificate?: boolean;
  hasSslKey?: boolean;
  cognitoUsername?: string;
  email?: string;
  displayName?: string;
  permissionLevel?: string;
}

export interface Maintainer {
  cognitoUsername: string;
  email: string;
  displayName?: string;
  permissionLevel?: string;
  solutions?: string[];
  createdAt?: string;
}

export interface UnitMaintainer {
  tenantId: string;
  solutionId: string;
  unitId: string;
  cognitoUsername: string;
  email: string;
  displayName?: string;
  permissionLevel: string;
  assignedAt?: string;
}

export interface MaintainerAssignment {
  tenantId: string;
  solutionId: string;
  unitId: string;
  permissionLevel: string;
}

export interface UnitUser {
  cognitoUsername: string;
  email: string;
  displayName?: string;
  permissionLevel?: string;
}

export interface UnitDbConfig {
  useIntegratedDatabase: boolean;
  dbHost?: string;
  dbPort?: number;
  dbDatabase?: string;
  dbSchema?: string;
  dbUsername?: string;
  dbPassword?: string;
  dbConnectionTimeout?: number;
  dbSslMode?: string;
  dbSslRootCert?: string;
  dbSslCertificate?: string;
  dbSslKey?: string;
}

// Define the custom baseQuery with reauth interceptor
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    const sessionID = localStorage.getItem('session_id');
    headers.set('Content-Type', 'application/json');
    if (sessionID) {
      headers.set('X-Session-ID', sessionID);
    }
    return headers;
  },
  credentials: 'include',
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && (result.error.status === 401 || result.error.status === 403)) {
    console.log('Session invalid via RTK Query, logging out');
    localStorage.removeItem('session_id');
    window.location.href = '/login';
    // Return a resolved promise that keeps code execution safe
    return new Promise(() => {}) as any;
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Tenant',
    'TenantDetail',
    'SolutionTemplate',
    'AvailableSolution',
    'AssignedSolution',
    'UnitList',
    'UnitUser',
    'UnitDbConfig',
    'UnitSearch',
    'Maintainer',
    'UnitMaintainers',
  ],
  endpoints: (builder) => ({
    // Tenants Endpoints
    getTenants: builder.query<ApiResponse<Tenant[]>, void>({
      query: () => '/tenants',
      providesTags: ['Tenant'],
    }),
    createTenant: builder.mutation<ApiResponse<Tenant>, string>({
      query: (name) => ({
        url: '/tenants',
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Tenant'],
    }),
    getTenant: builder.query<ApiResponse<Tenant>, string>({
      query: (tenantId) => `/tenants/${encodeURIComponent(tenantId)}`,
      providesTags: (result, error, tenantId) => [{ type: 'TenantDetail', id: tenantId }],
    }),

    // Solution Templates Endpoints
    getSolutionTemplates: builder.query<ApiResponse<SolutionTemplate[]>, void>({
      query: () => '/solution-templates',
      providesTags: ['SolutionTemplate'],
    }),
    createSolutionTemplate: builder.mutation<ApiResponse<any>, { code: string; name: string }>({
      query: ({ code, name }) => ({
        url: '/solution-templates',
        method: 'POST',
        body: { code, name },
      }),
      invalidatesTags: ['SolutionTemplate'],
    }),
    updateSolutionTemplate: builder.mutation<ApiResponse<any>, { code: string; name: string }>({
      query: ({ code, name }) => ({
        url: `/solution-templates/${encodeURIComponent(code)}`,
        method: 'PUT',
        body: { name },
      }),
      invalidatesTags: ['SolutionTemplate'],
    }),
    deleteSolutionTemplate: builder.mutation<ApiResponse<any>, string>({
      query: (code) => ({
        url: `/solution-templates/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SolutionTemplate'],
    }),

    // Solution Assignment Endpoints
    getAvailableSolutions: builder.query<ApiResponse<Array<{ code: string; name: string }>>, void>({
      query: () => '/solutions',
      providesTags: ['AvailableSolution'],
    }),
    getAssignedSolutions: builder.query<ApiResponse<AssignedSolution[]>, string>({
      query: (tenantId) => `/tenants/${encodeURIComponent(tenantId)}/solutions`,
      providesTags: (result, error, tenantId) => [
        { type: 'AssignedSolution', id: tenantId },
      ],
    }),
    assignSolution: builder.mutation<ApiResponse<AssignedSolution>, { tenantId: string; solutionCode: string }>({
      query: ({ tenantId, solutionCode }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/assign`,
        method: 'POST',
        body: { solutionCode },
      }),
      invalidatesTags: (result, error, { tenantId }) => [
        { type: 'AssignedSolution', id: tenantId },
        { type: 'TenantDetail', id: tenantId },
        'AvailableSolution',
      ],
    }),

    // Units Endpoints
    getUnits: builder.query<ApiResponse<Unit[]>, { tenantId: string; solutionId: string }>({
      query: ({ tenantId, solutionId }) =>
        `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units`,
      providesTags: (result, error, { tenantId, solutionId }) => [
        { type: 'UnitList', id: `${tenantId}-${solutionId}` },
      ],
    }),
    createUnit: builder.mutation<ApiResponse<Unit>, { tenantId: string; solutionId: string; name: string }>({
      query: ({ tenantId, solutionId, name }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: (result, error, { tenantId, solutionId }) => [
        { type: 'UnitList', id: `${tenantId}-${solutionId}` },
      ],
    }),
    updateUnitStatus: builder.mutation<ApiResponse<any>, { tenantId: string; solutionId: string; unitId: string; disabled: boolean }>({
      query: ({ tenantId, solutionId, unitId, disabled }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/status`,
        method: 'PUT',
        body: { disabled },
      }),
      invalidatesTags: (result, error, { tenantId, solutionId }) => [
        { type: 'UnitList', id: `${tenantId}-${solutionId}` },
      ],
    }),

    // Unit Database Configuration Endpoints
    getUnitDbConfig: builder.query<ApiResponse<any>, { tenantId: string; solutionId: string; unitId: string }>({
      query: ({ tenantId, solutionId, unitId }) =>
        `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/db-config`,
      providesTags: (result, error, { unitId }) => [{ type: 'UnitDbConfig', id: unitId }],
    }),
    updateUnitDbConfig: builder.mutation<ApiResponse<any>, { tenantId: string; solutionId: string; unitId: string; payload: any }>({
      query: ({ tenantId, solutionId, unitId, payload }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/db-config`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, { tenantId, solutionId, unitId }) => [
        { type: 'UnitDbConfig', id: unitId },
        { type: 'UnitList', id: `${tenantId}-${solutionId}` },
      ],
    }),

    // Unit User Assignment Endpoints
    getUnitUser: builder.query<ApiResponse<UnitUser | null>, { tenantId: string; solutionId: string; unitId: string }>({
      query: ({ tenantId, solutionId, unitId }) =>
        `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/user`,
      providesTags: (result, error, { unitId }) => [{ type: 'UnitUser', id: unitId }],
    }),
    assignUserToUnit: builder.mutation<
      ApiResponse<any>,
      {
        tenantId: string;
        solutionId: string;
        unitId: string;
        cognitoUsername: string;
        email: string;
        displayName?: string;
        password?: string;
        permissionLevel?: string;
      }
    >({
      query: ({ tenantId, solutionId, unitId, cognitoUsername, email, displayName, password, permissionLevel }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/assign`,
        method: 'POST',
        body: { cognitoUsername, email, displayName, password, permissionLevel },
      }),
      invalidatesTags: (result, error, { tenantId, solutionId, unitId }) => [
        { type: 'UnitUser', id: unitId },
        { type: 'UnitList', id: `${tenantId}-${solutionId}` },
      ],
    }),
    unassignUserFromUnit: builder.mutation<ApiResponse<any>, { tenantId: string; solutionId: string; unitId: string }>({
      query: ({ tenantId, solutionId, unitId }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/unassign`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { tenantId, solutionId, unitId }) => [
        { type: 'UnitUser', id: unitId },
        { type: 'UnitList', id: `${tenantId}-${solutionId}` },
      ],
    }),

    // Search Endpoints
    searchUnitsByCognito: builder.query<ApiResponse<any[]>, string>({
      query: (cognitoUsername) => `/units/search?cognitoUsername=${encodeURIComponent(cognitoUsername)}`,
      providesTags: ['UnitSearch'],
    }),
    searchUnitsByHierarchy: builder.query<ApiResponse<any[]>, string>({
      query: (hierarchyPattern) => `/units/search?hierarchyPattern=${encodeURIComponent(hierarchyPattern)}`,
      providesTags: ['UnitSearch'],
    }),

    // Maintainers Endpoints
    getMaintainers: builder.query<ApiResponse<Maintainer[]>, void>({
      query: () => '/maintainers',
      providesTags: ['Maintainer'],
    }),
    createMaintainer: builder.mutation<ApiResponse<Maintainer>, Partial<Maintainer> & { password?: string }>({
      query: (maintainer) => ({
        url: '/maintainers',
        method: 'POST',
        body: maintainer,
      }),
      invalidatesTags: ['Maintainer'],
    }),
    updateMaintainer: builder.mutation<
      ApiResponse<any>,
      { username: string; email?: string; displayName?: string }
    >({
      query: ({ username, email, displayName }) => ({
        url: `/maintainers/${encodeURIComponent(username)}`,
        method: 'PUT',
        body: { email, displayName },
      }),
      invalidatesTags: ['Maintainer'],
    }),
    deleteMaintainer: builder.mutation<ApiResponse<any>, string>({
      query: (username) => ({
        url: `/maintainers/${encodeURIComponent(username)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Maintainer'],
    }),

    listAllUnits: builder.query<ApiResponse<Unit[]>, void>({
      query: () => '/units',
      providesTags: ['UnitList'],
    }),
    getMaintainerUnits: builder.query<ApiResponse<UnitMaintainer[]>, string>({
      query: (username) => `/maintainers/${encodeURIComponent(username)}/units`,
      providesTags: (result, error, username) => [{ type: 'Maintainer', id: `${username}-units` }],
    }),
    updateMaintainerAssignments: builder.mutation<
      ApiResponse<any>,
      { username: string; assignments: MaintainerAssignment[] }
    >({
      query: ({ username, assignments }) => ({
        url: `/maintainers/${encodeURIComponent(username)}/assignments`,
        method: 'POST',
        body: { assignments },
      }),
      invalidatesTags: (result, error, { username }) => [
        'Maintainer',
        { type: 'Maintainer', id: `${username}-units` },
        'UnitMaintainers',
      ],
    }),

    // Unit Maintainer Endpoints
    getUnitMaintainers: builder.query<ApiResponse<UnitMaintainer[]>, { tenantId: string; solutionId: string; unitId: string }>({
      query: ({ tenantId, solutionId, unitId }) =>
        `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/maintainers`,
      providesTags: (result, error, { unitId }) => [{ type: 'UnitMaintainers', id: unitId }],
    }),
    assignMaintainerToUnit: builder.mutation<
      ApiResponse<UnitMaintainer>,
      {
        tenantId: string;
        solutionId: string;
        unitId: string;
        cognitoUsername: string;
        permissionLevel: string;
      }
    >({
      query: ({ tenantId, solutionId, unitId, cognitoUsername, permissionLevel }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/maintainers`,
        method: 'POST',
        body: { cognitoUsername, permissionLevel },
      }),
      invalidatesTags: (result, error, { unitId }) => [
        { type: 'UnitMaintainers', id: unitId },
      ],
    }),
    unassignMaintainerFromUnit: builder.mutation<ApiResponse<any>, { tenantId: string; solutionId: string; unitId: string; username: string }>({
      query: ({ tenantId, solutionId, unitId, username }) => ({
        url: `/tenants/${encodeURIComponent(tenantId)}/solutions/${encodeURIComponent(solutionId)}/units/${encodeURIComponent(unitId)}/maintainers/${encodeURIComponent(username)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { unitId }) => [
        { type: 'UnitMaintainers', id: unitId },
      ],
    }),
  }),
});

export const {
  useGetTenantsQuery,
  useCreateTenantMutation,
  useGetTenantQuery,
  useGetSolutionTemplatesQuery,
  useCreateSolutionTemplateMutation,
  useUpdateSolutionTemplateMutation,
  useDeleteSolutionTemplateMutation,
  useGetAvailableSolutionsQuery,
  useGetAssignedSolutionsQuery,
  useAssignSolutionMutation,
  useGetUnitsQuery,
  useCreateUnitMutation,
  useUpdateUnitStatusMutation,
  useGetUnitDbConfigQuery,
  useUpdateUnitDbConfigMutation,
  useGetUnitUserQuery,
  useAssignUserToUnitMutation,
  useUnassignUserFromUnitMutation,
  useLazySearchUnitsByCognitoQuery,
  useLazySearchUnitsByHierarchyQuery,
  useGetMaintainersQuery,
  useCreateMaintainerMutation,
  useUpdateMaintainerMutation,
  useDeleteMaintainerMutation,
  useGetUnitMaintainersQuery,
  useAssignMaintainerToUnitMutation,
  useUnassignMaintainerFromUnitMutation,
  useListAllUnitsQuery,
  useGetMaintainerUnitsQuery,
  useUpdateMaintainerAssignmentsMutation,
} = apiSlice;
